import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { merge } from "lodash";
import { isFetchResult, reusableFetch } from "@/utils/useFetch";
import {
  IServiceSlice,
  IServiceState,
  IGetAllServicesResponse,
  IUpdateServiceStatusResponse,
} from "@/store/slices/types/serviceSliceTypes";

export const getAllServices = createAsyncThunk<IGetAllServicesResponse, unknown>(
  "service",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(`service/all`, "POST", data, rejectWithValue);
    return response as IGetAllServicesResponse;
  },
);

export const updateServiceTransactionChargesStatus = createAsyncThunk<
  IUpdateServiceStatusResponse,
  { serviceId: string; value: boolean }
>("service/updateTransactionChargesStatus", async ({ serviceId, value }, { rejectWithValue }) => {
  const response = await reusableFetch(
    `service/transaction-charges-status/${serviceId}`,
    "PATCH",
    { is_transaction_charges_applicable: value },
    rejectWithValue,
  );
  if (!isFetchResult(response)) {
    return rejectWithValue(response);
  }
  return response.data as IUpdateServiceStatusResponse;
});

const initialState: IServiceState = {
  gettingServices: false,
  page: 1,
  services: {
    list: [],
  },
};

export const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    saveValue: (state, action) => merge(state, action.payload),
    resetServiceState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllServices.fulfilled, (state, { payload }: PayloadAction<IGetAllServicesResponse>) => {
        state.services = payload?.data?.data || ({} as IServiceState["services"]);
        state.gettingServices = false;
      })
      .addCase(getAllServices.pending, (state) => {
        state.gettingServices = true;
      })
      .addCase(getAllServices.rejected, (state) => {
        state.gettingServices = false;
      })
      // Updates a single service's status field in place so the list is not re-fetched
      .addCase(
        updateServiceTransactionChargesStatus.fulfilled,
        (state, { payload }: PayloadAction<IUpdateServiceStatusResponse>) => {
          const updated = payload?.data;
          if (updated?._id && Array.isArray(state.services?.list)) {
            state.services.list = state.services.list.map((service) =>
              service._id === updated._id ? { ...service, ...updated } : service,
            );
          }
        },
      );
  },
});

export const { saveValue, resetServiceState } = serviceSlice.actions;

export const serviceStateSelector = (state: IServiceSlice) => state.service;
export const serviceSelector = (state: IServiceSlice) => state.service.services;
